import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { FiChevronLeft, FiChevronRight, FiEye, FiPrinter, FiDownload } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';

const PaymentHistoryTable = () => {
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');
  const packageId = searchParams.get('packageId');

  // Function to find the latest receipt
  const getLatestReceipt = () => {
    if (payments.length === 0) return null;

    // Sort payments by date (newest first)
    const sortedPayments = [...payments].sort((a, b) =>
      new Date(b.date) - new Date(a.date)
    );

    return sortedPayments[0];
  };

  const navigate = useNavigate();

  // Format date and time to readable string
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return payments.slice(startIndex, endIndex);
  };

  useEffect(() => {
    if (customerId) {
      fetchPaymentHistory(customerId);
    }
  }, [customerId]);

  const fetchPaymentHistory = async (customerId) => {
    setIsLoading(true);
    try {
      const response = await sendApiRequest(
        `api/admin/payment-history?customerId=${customerId}&packageId=${packageId}`,
        {},
        "GET"
      );


      if (response.status === 200) {
        toast.success(response.data.message);
        const data = response.data.data;
        setPayments(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error('Failed to fetch payment history');
    } finally {
      setIsLoading(false);
    }
  };



  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrintReceipt = (payment) => {
    if (payment.receiptId) {
      // Use URL params for direct receipt view (new method)
      localStorage.setItem('paymentReceiptFromOnce', JSON.stringify(payment));
      navigate('/admin/packages/view-receipt');
    } else {
      // Fall back to localStorage if receiptId is not available
      localStorage.setItem('paymentReceiptFromOnce', JSON.stringify(payment));
      navigate('/admin/packages/view-receipt');
    }
  };

  // Function to view the latest receipt
  const handleViewLatestReceipt = () => {
    const latestReceipt = getLatestReceipt();
    if (latestReceipt) {
      localStorage.setItem('paymentReceiptFromOnce', JSON.stringify(latestReceipt));
      navigate('/admin/packages/view-receipt');
    } else {
      toast.error('No receipts found');
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm('Are you sure you want to delete this payment entry? This will also delete all linked commissions.')) {
      return;
    }

    try {
      setIsLoading(true);
      await sendApiRequest(
        `api/packages/payment-entry/${payment.receiptId}/${payment._id}`,
        {},
        'DELETE'
      );
      toast.success('Payment entry deleted successfully');
      // Refresh payment history
      if (customerId) {
        await fetchPaymentHistory(customerId);
      }
    } catch (error) {
      console.error('Error deleting payment entry:', error);
      toast.error('Failed to delete payment entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const tableRowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-6 p-4 md:p-6 bg-gray-50 rounded-xl">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
        {payments.length > 0 && (
          <button
            onClick={handleViewLatestReceipt}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            <FiEye />
            <span>Latest Receipt</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-gray-500">No payment history found</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Receipt #
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Package
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Partner
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Paid
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Total
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Balance
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Payment Mode
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getCurrentPageItems().map((payment, index) => (
                      <motion.tr
                        key={payment._id}
                        variants={tableRowVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatDate(payment.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                          {payment.receiptNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                            {payment.package?.title || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.customer?.customerName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.partner?.fullName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                          {formatCurrency(payment.totalAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${payment.remainingAmount > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                            {formatCurrency(payment.remainingAmount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="capitalize">{payment.paymentMode?.toLowerCase() || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handlePrintReceipt(payment)}
                            className="text-gray-600 hover:text-gray-900 bg-gray-100 p-1 rounded-full hover:bg-gray-50"
                            title="See Receipt"
                          >
                            See Receipt
                          </button>
                          <button
                            onClick={() => handleDeletePayment(payment)}
                            className="text-red-600 hover:text-red-900 bg-red-100 p-1 rounded-full hover:bg-red-50"
                            title="Delete Payment"
                          >
                            Delete
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-500">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, payments.length)}</span> of{' '}
              <span className="font-medium">{payments.length}</span> payments
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center"
              >
                <FiChevronLeft className="mr-1" /> Previous
              </button>
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <span className="px-4 py-2 text-gray-500">...</span>
                )}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                )}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center"
              >
                Next <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHistoryTable;